// Genetics journal: persists discovered emergent trait IDs across runs and
// derives unlockable preset starting genomes from milestone discovery counts.
// Pure(-ish) module: injectable Storage for headless testing.

import type { Genome } from '../sim/genome';
import { ALLELE_MAX, HIDDEN_GENE_COUNT } from '../sim/genome';
import { BASE_TRAITS, traitIndex } from '../sim/traits/base';

export const JOURNAL_KEY = 'ballBreeder_journal';

const GENOME_LEN = BASE_TRAITS.length + HIDDEN_GENE_COUNT; // 21 + 4 = 25
const MID = Math.round(ALLELE_MAX / 2); // 8

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Build a genome with named traits set to MAX/MIN, everything else at MID. */
function buildGenome(highs: string[], lows: string[] = []): Genome {
  const g: number[] = new Array(GENOME_LEN).fill(MID);
  for (const id of highs) {
    const i = traitIndex(id);
    if (i >= 0) g[i] = ALLELE_MAX;
  }
  for (const id of lows) {
    const i = traitIndex(id);
    if (i >= 0) g[i] = 0;
  }
  return g;
}

// ---------------------------------------------------------------------------
// Preset definitions
// ---------------------------------------------------------------------------

const SPEED_FLOCK_GENOMES: Genome[] = [
  buildGenome(['speed', 'attackRate', 'projectileSpeed'], ['mass', 'armor']),
  buildGenome(['speed', 'alignment', 'cohesion'], ['mass', 'size']),
  buildGenome(['speed', 'attackDamage', 'attackRange'], ['mass']),
  buildGenome(['speed', 'pickupAttraction', 'maturationRate'], ['armor']),
];

const TANK_SQUAD_GENOMES: Genome[] = [
  buildGenome(['maxHealth', 'armor', 'regen'], ['speed', 'attackRate']),
  buildGenome(['maxHealth', 'mass', 'armor'], ['speed', 'maturationRate']),
  buildGenome(['maxHealth', 'regen', 'lifespan'], ['attackDamage', 'speed']),
  buildGenome(['maxHealth', 'armor', 'cohesion', 'regen'], ['speed']),
];

const ELITE_GENETICS_GENOMES: Genome[] = [
  buildGenome(['speed', 'maxHealth', 'attackDamage', 'armor']),
  buildGenome(['attackRate', 'attackRange', 'regen', 'cohesion']),
  buildGenome(['speed', 'lifespan', 'projectileSpeed', 'pickupAttraction']),
  buildGenome(['maxHealth', 'attackDamage', 'regen', 'attackRate']),
];

export interface Preset {
  name: string;
  genomes: Genome[];
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/** Read the list of discovered emergent trait IDs. Returns [] on missing/corrupt. */
export function getJournal(storage: Storage = localStorage): string[] {
  const raw = storage.getItem(JOURNAL_KEY);
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((x): x is string => typeof x === 'string');
  } catch {
    return [];
  }
}

/** Add a discovered trait ID. No-op if already present. Persists immediately. */
export function addDiscovery(traitId: string, storage: Storage = localStorage): void {
  const journal = getJournal(storage);
  if (journal.includes(traitId)) return;
  journal.push(traitId);
  storage.setItem(JOURNAL_KEY, JSON.stringify(journal));
}

/**
 * Return presets unlocked by the player's discovery count:
 *   3  → Speed Flock
 *   6  → Tank Squad
 *   10 → Elite Genetics
 */
export function getUnlockedPresets(storage: Storage = localStorage): Preset[] {
  const count = getJournal(storage).length;
  const presets: Preset[] = [];
  if (count >= 3) presets.push({ name: 'Speed Flock', genomes: SPEED_FLOCK_GENOMES });
  if (count >= 6) presets.push({ name: 'Tank Squad', genomes: TANK_SQUAD_GENOMES });
  if (count >= 10) presets.push({ name: 'Elite Genetics', genomes: ELITE_GENETICS_GENOMES });
  return presets;
}
